import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { uniq } from "../../utils/nativeUtils";
import { Fragment, useContext, useLayoutEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { ReactComponent as ChevronRight } from "../../assets/icons/chevron-right.svg";
import { UIStateStoreContext } from "../../stores/uiStateStore";
import { Tags } from "./Tags";
import { ServiceEnum } from "../../models/dataModel";
import { resolveLanguage } from "../../utils/functions";
const URLCrumbs = observer(({ targetURL }) => {
    const uiStore = useContext(UIStateStoreContext);
    const containerRef = useRef(null);
    const [maxParts, setMaxParts] = useState(5);
    const prevUrl = useRef('');
    // Decode the URL to make it more readable for users
    targetURL = decodeURI(targetURL);
    function isLanguageCode(part) {
        return !!resolveLanguage(part, uiStore.getService() || ServiceEnum.TRIMBLE_HELP);
    }
    function getParts() {
        let parts = targetURL.substring(8).split("/"); // Remove 'https://'
        parts.pop(); // Remove last part (same as the title)
        parts = uniq(parts); // Remove duplicates
        // If language code is present, remove it
        const languagePartIndex = parts.findIndex(part => isLanguageCode(part));
        if (languagePartIndex !== -1) {
            parts.splice(languagePartIndex, 1);
        }
        // If there are more than maxParts parts, keep only the first (maxParts - 1) and the last one
        if (parts.length > maxParts) {
            parts = [...parts.slice(0, maxParts - 1), "...", parts[parts.length - 1]];
        }
        return parts;
    }
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el)
            return;
        const urlChanged = prevUrl.current !== targetURL;
        prevUrl.current = targetURL;
        // If URL changed and we're not yet showing 5 parts, reset to 5 first so we
        // can check whether the new URL's crumbs fit on one row
        if (urlChanged && maxParts !== 5) {
            setMaxParts(5);
            return;
        }
        // Only measure when showing 5 parts; if the row wraps, switch to 4
        if (maxParts === 5) {
            const firstSpan = el.querySelector("span");
            if (firstSpan && el.offsetHeight > firstSpan.offsetHeight * 2) {
                setMaxParts(4);
            }
        }
    }, [targetURL, maxParts]);
    return (_jsx("div", { className: "url-crumbs", "data-testid": "urlCrumbs", ref: containerRef, children: getParts().map((crumb, i) => {
            const lastPart = i + 1 === getParts().length;
            return (_jsxs(Fragment, { children: [_jsx("span", { children: crumb }), !lastPart && _jsx(ChevronRight, {})] }, i));
        }) }));
});
/**
 * Renders a result component.
 * @param result result to display
 */
export const ResultBox = ({ result }) => {
    const resultURL = !!result.url && typeof result.url.raw === "string" ? result.url.raw : "";
    const redirectURL = !!result.url && !!result.url.withRedirect && result.url.withRedirect;
    const teaser = !!result.teaser && !!result.teaser.snippet
        ? result.teaser.snippet.trimEnd()
        : result.teaser.raw
            ? result.teaser.raw.trimEnd()
            : undefined;
    const styledTeaser = teaser?.endsWith(".") ? teaser : teaser + " ...";
    const showResultBody = !!teaser || !!result.preview_image_url;
    return (_jsxs("div", { className: "sui-result", "data-testid": "resultItem", children: [_jsxs("div", { className: "result-header", children: [_jsx("h4", { children: _jsx("a", { href: redirectURL || resultURL, target: "_blank", rel: "noopener noreferrer", "data-testid": "result-title", children: result.title.raw }) }), _jsx(URLCrumbs, { targetURL: resultURL })] }), showResultBody && (_jsx("div", { className: "result-body", children: !!styledTeaser && (_jsx("div", { className: "result-teaser", children: _jsx("p", { dangerouslySetInnerHTML: { __html: styledTeaser }, "data-testid": "resultTeaser" }) })) })), _jsx("div", { className: "result-footer", children: _jsx(Tags, { result: result }) })] }));
};
